import { CoverTemplate, CoverTemplateValues } from '../types';
import { getDefaultCoverValues, buildDocumentFromCoverSections } from './templateUtils';
import { DocumentSection, TextBlock, ImageBlock, TableBlock } from '../../core/ast/document.types';

export const classicAcademicTemplate: CoverTemplate = {
  id: 'tpl-classic-academic',
  name: 'Classic Academic Formal',
  category: 'general_academic',
  description: 'Traditional formal institutional layout featuring centered crest, address, double border rules, and structured submission box.',
  thumbnailBadge: 'Classic',
  version: 3,
  supportedFields: [
    'universityName', 'universityAddress', 'departmentName', 'logoAssetId', 'showLogo',
    'documentTypeLabel', 'title', 'subtitle',
    'courseCode', 'courseTitle', 'sectionOrGroup',
    'studentName', 'studentId', 'facultyName', 'facultyDesignation',
    'submissionDate', 'semester', 'academicYear',
  ],
  defaultValues: getDefaultCoverValues({
    documentTypeLabel: 'Term Assignment',
    title: '',
    subtitle: '',
    courseCode: '',
    courseTitle: '',
    sectionOrGroup: '',
    studentName: '',
    studentId: '',
    facultyName: '',
    facultyDesignation: '',
    submissionDate: '',
    borderStyle: 'double',
  }),
  compileToDocument: (values: CoverTemplateValues, pageSettings) => {
    const now = new Date().toISOString();
    const blocks: Array<TextBlock | ImageBlock | TableBlock> = [];

    // 1. Center Logo
    if (values.showLogo && values.logoAssetId) {
      blocks.push({
        id: 'blk-cover-logo',
        type: 'image',
        createdAt: now,
        data: {
          assetId: values.logoAssetId,
          previewDataUrl: values.logoAssetId,
          widthPercent: 26,
          alignment: 'center',
          altText: values.universityName || 'East West University',
        },
      });
    }

    // 2. University Name (Center Aligned)
    blocks.push({
      id: 'blk-cover-univ',
      type: 'text',
      createdAt: now,
      data: {
        text: (values.universityName || 'EAST WEST UNIVERSITY').toUpperCase(),
        formatting: { bold: true, fontSizePt: 16, alignment: 'center', color: '#111827' },
      },
    });

    // 3. Address (Center Aligned)
    blocks.push({
      id: 'blk-cover-addr',
      type: 'text',
      createdAt: now,
      data: {
        text: values.universityAddress || 'A/2 Jahurul Islam Ave, Dhaka 1212',
        formatting: { fontSizePt: 10, alignment: 'center', color: '#4a5563' },
      },
    });

    // Spacing
    blocks.push({
      id: 'blk-cover-sp1',
      type: 'text',
      createdAt: now,
      data: { text: '\n\n', formatting: { fontSizePt: 12 } },
    });

    // 4. Document Title Section
    if (values.documentTypeLabel) {
      blocks.push({
        id: 'blk-cover-type',
        type: 'text',
        createdAt: now,
        data: {
          text: `— ${values.documentTypeLabel.toUpperCase()} —`,
          formatting: { bold: true, fontSizePt: 11, alignment: 'center', color: '#1e3a8a' },
        },
      });
    }

    if (values.title) {
      blocks.push({
        id: 'blk-cover-title',
        type: 'text',
        createdAt: now,
        data: {
          text: values.title,
          formatting: { bold: true, fontSizePt: 20, alignment: 'center', color: '#111827' },
        },
      });
    }

    if (values.subtitle) {
      blocks.push({
        id: 'blk-cover-sub',
        type: 'text',
        createdAt: now,
        data: {
          text: values.subtitle,
          formatting: { italic: true, fontSizePt: 12, alignment: 'center', color: '#4b5563' },
        },
      });
    }

    // Spacing
    blocks.push({
      id: 'blk-cover-sp2',
      type: 'text',
      createdAt: now,
      data: { text: '\n', formatting: { fontSizePt: 10 } },
    });

    // 5. Course Details
    const courseCodeStr = values.courseCode ? `Course: ${values.courseCode}` : '';
    const courseTitleStr = values.courseTitle ? `— ${values.courseTitle}` : '';
    const secStr = values.sectionOrGroup ? `Section: ${values.sectionOrGroup}` : '';
    const semStr = [values.semester, values.academicYear].filter(Boolean).join(' ');

    const line1 = [courseCodeStr, courseTitleStr].filter(Boolean).join(' ');
    const line2 = [secStr, semStr].filter(Boolean).join(' | ');
    const courseText = [line1, line2].filter(Boolean).join('\n');

    if (courseText) {
      blocks.push({
        id: 'blk-cover-course',
        type: 'text',
        createdAt: now,
        data: {
          text: courseText,
          formatting: { bold: true, fontSizePt: 11, alignment: 'center', color: '#374151' },
        },
      });
    }

    // Spacing
    blocks.push({
      id: 'blk-cover-sp3',
      type: 'text',
      createdAt: now,
      data: { text: '\n', formatting: { fontSizePt: 10 } },
    });

    // 6. Student & Faculty Submission Table
    blocks.push({
      id: 'blk-cover-sub-table',
      type: 'table',
      createdAt: now,
      data: {
        rows: 2,
        cols: 2,
        hasHeaderRow: true,
        hasHeaderCol: false,
        borderStyle: 'academic',
        cells: [
          [
            { id: 'c00', content: 'SUBMITTED BY', isHeader: true, bold: true, alignment: 'left' },
            { id: 'c01', content: 'SUBMITTED TO', isHeader: true, bold: true, alignment: 'left' },
          ],
          [
            {
              id: 'c10',
              content: `Name: ${values.studentName || ''}\nID: ${values.studentId || ''}\nEmail: ${values.studentEmail || ''}`,
              alignment: 'left',
            },
            {
              id: 'c11',
              content: `Name: ${values.facultyName || ''}\nDesignation: ${values.facultyDesignation || ''}\nDepartment: ${values.facultyDepartment || values.departmentName || ''}`,
              alignment: 'left',
            },
          ],
        ],
      },
    });

    // 7. Submission Date
    if (values.submissionDate) {
      blocks.push({
        id: 'blk-cover-date',
        type: 'text',
        createdAt: now,
        data: {
          text: `\nSubmission Date: ${values.submissionDate}`,
          formatting: { italic: true, fontSizePt: 11, alignment: 'center', color: '#6b7280' },
        },
      });
    }

    const coverSection: DocumentSection = {
      id: 'sec-cover-main',
      title: 'Cover Page',
      type: 'cover',
      enabled: true,
      collapsed: false,
      blocks,
    };

    return buildDocumentFromCoverSections(values, [coverSection], pageSettings);
  },
};
